import React, { useEffect, useMemo, useState } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { getCallQueue, getDealById, getPriorityLane, logCallOutcome, queueAction, type LaneItem, type PriorityLane, type QueueItem } from './crmApi'
import { StageBadge, crmToast, CrmToaster, IconAlert, IconClock, IconPhone, SearchInput, InitialBox } from './crmUi'
import { QuickBookButton } from './BookingModal'
import { DealDetail } from './DealDetail'
import { LeadRedirect } from './CrmLeadPage'
import { ForcedQualificationModal, QualStatusChip } from './DealQualification'

/**
 * Call Queue (Lee, 31 Jul 2026 — PRD-04 Phase 2): the "who do I ring next" list.
 * Leads drop in automatically on five triggers; the team works it as a shared pool:
 * claim → dial (tel:) → outcome. Ordering is priority (money at risk first) then age.
 * Outcomes follow the approved defaults: no-answer retries next working day (max 3),
 * callback snoozes to the chosen time, spoke/booked need a note (forced-log
 * discipline), bad number removes.
 */

const TRIGGER_META: Record<QueueItem['trigger'], { label: string; bg: string; fg: string }> = {
  payment_failed: { label: 'Payment failed', bg: '#FEE2E2', fg: '#991B1B' },
  card_removed: { label: 'Card removed', bg: '#FFE4E6', fg: '#9F1239' },
  trial_expired: { label: 'Trial expired', bg: '#FFEDD5', fg: '#9A3412' },
  auto_trial: { label: 'New trial', bg: '#DBEAFE', fg: '#1E40AF' },
  imported: { label: 'Imported', bg: '#F1F5F9', fg: '#334155' },
  // The qualification cold-call pool — every not-yet-qualified pre-trial lead,
  // auto-synced. Spoke/booked outcomes here force the qualification form.
  unqualified: { label: 'Unqualified', bg: '#EDE9FE', fg: '#5B21B6' },
}
const OUTCOMES: { key: string; label: string }[] = [
  { key: 'no_answer', label: 'No answer' },
  { key: 'callback', label: 'Callback…' },
  { key: 'spoke', label: 'Spoke' },
  { key: 'booked', label: 'Booked' },
  { key: 'bad_number', label: 'Bad number' },
  // Lee, 22 Sep 2026: a call is NOT mandatory when the company was already
  // demoed — logging this resolves the entry, stamps contacted + discovery.
  { key: 'already_demoed', label: 'Already demoed' },
]

/* ── Funnel Gate 1: the priority lane (spec §9/§10, 17 Aug 2026) ─────────────
 * Fresh signups bypass the whole queue and sit here, most urgent first, with a
 * LIVE second-by-second timer: green 0–5m · amber 5–15m · red 15–60m ·
 * critical 60m+ (pulsing — different in kind, not shade). The server computes
 * the covered-hours clock; the client only ticks. */

const LANE_STYLE: Record<string, { bg: string; fg: string; label: string }> = {
  waiting: { bg: '#F1F5F9', fg: '#64748B', label: 'CLOCK STARTS 09:00' },
  green: { bg: '#DCFCE7', fg: '#166534', label: 'ON TARGET' },
  amber: { bg: '#FEF3C7', fg: '#92400E', label: 'SLIPPING' },
  red: { bg: '#FEE2E2', fg: '#991B1B', label: 'TARGET MISSED' },
  critical: { bg: '#7F1D1D', fg: '#FECACA', label: 'AT RISK' },
}
const stateNow = (clockStartAt: string): { state: LaneItem['state']; mins: number; secs: number } => {
  const ms = Date.now() - new Date(clockStartAt).getTime()
  if (ms < 0) return { state: 'waiting', mins: 0, secs: 0 }
  const mins = Math.floor(ms / 60000)
  return { state: mins < 5 ? 'green' : mins < 15 ? 'amber' : mins < 60 ? 'red' : 'critical', mins, secs: Math.floor((ms % 60000) / 1000) }
}

const LiveTimer: React.FC<{ clockStartAt: string }> = ({ clockStartAt }) => {
  const [, force] = useState(0)
  useEffect(() => { const t = setInterval(() => force((n) => n + 1), 1000); return () => clearInterval(t) }, [])
  const { state, mins, secs } = stateNow(clockStartAt)
  const s = LANE_STYLE[state]
  const shown = state === 'waiting' ? '—' : mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins}:${String(secs).padStart(2, '0')}`
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg font-mono font-bold text-[13px] ${state === 'critical' ? 'animate-pulse ring-2 ring-red-500' : ''}`}
      style={{ background: s.bg, color: s.fg }}
      title={`${s.label} — clock started ${new Date(clockStartAt).toLocaleTimeString()}`}
    >
      <IconClock className="w-3.5 h-3.5" /> {shown}
    </span>
  )
}

const PriorityLaneSection: React.FC<{ onOpenDeal: (dealId: string) => void; onLogged: () => void }> = ({ onOpenDeal, onLogged }) => {
  const [lane, setLane] = useState<PriorityLane | null>(null)
  const [outcomeFor, setOutcomeFor] = useState<LaneItem | null>(null)
  const load = () => getPriorityLane().then(setLane).catch(() => {})
  useEffect(() => { load(); const t = setInterval(load, 30_000); return () => clearInterval(t) }, [])

  if (!lane || lane.items.length === 0) return null
  const worst = lane.counts.critical > 0 ? 'critical' : lane.counts.red > 0 ? 'red' : 'green'
  return (
    <div className={`mx-6 mt-4 rounded-xl border-2 overflow-hidden ${worst === 'critical' ? 'border-red-600' : worst === 'red' ? 'border-red-400' : 'border-emerald-400'}`}>
      <div className="px-4 py-2.5 flex items-center justify-between" style={{ background: worst === 'critical' ? '#7F1D1D' : worst === 'red' ? '#991B1B' : '#065F46' }}>
        <p className="text-[13px] font-bold text-white">
          <IconAlert className="w-4 h-4 inline -mt-0.5 mr-1" />NEW SIGNUPS — call within 5 minutes ({lane.counts.total} uncontacted
          {lane.counts.red + lane.counts.critical > 0 ? ` · ${lane.counts.red + lane.counts.critical} overdue` : ''})
        </p>
        <p className="text-[10px] text-white/70">{lane.coverHours} · a signup is at their computer RIGHT NOW</p>
      </div>
      <div className="divide-y divide-border bg-surface">
        {lane.items.map((it) => (
          <div key={it.dealId} className="px-4 py-2.5">
            <div className="flex items-center gap-3 flex-wrap">
              <LiveTimer clockStartAt={it.clockStartAt} />
              <button onClick={() => onOpenDeal(it.dealId)} className="min-w-0 text-left">
                <p className="text-sm font-bold text-txt-primary truncate">{it.name}</p>
                <p className="text-[11px] text-txt-tertiary truncate">
                  {it.contactName || '—'}{it.country ? ` · ${it.country}` : ''} · signed up {new Date(it.signedUpAt).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                  {it.attempted ? ' · attempted, not connected' : ''}
                </p>
              </button>
              <span className="flex-1" />
              {it.phone ? (
                <a href={`tel:${it.phone.replace(/[\s()-]/g, '')}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700"><IconPhone className="w-3.5 h-3.5" /> {it.phone}</a>
              ) : (
                <span className="px-2.5 py-1.5 rounded-lg text-[11px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">No phone — open the lead</span>
              )}
              <button
                onClick={() => setOutcomeFor(outcomeFor?.dealId === it.dealId ? null : it)}
                className="px-2.5 py-1.5 rounded-lg text-[11px] font-semibold border border-border text-txt-primary hover:bg-surface-hover"
              >
                Log outcome
              </button>
            </div>
            {outcomeFor?.dealId === it.dealId && it.queueItemId && (
              <div className="mt-2">
                <OutcomePanel
                  item={{ id: it.queueItemId, dealId: it.dealId, dealName: it.name, trigger: 'auto_trial' } as QueueItem}
                  onDone={() => { setOutcomeFor(null); load(); onLogged() }}
                  onCancel={() => setOutcomeFor(null)}
                />
              </div>
            )}
            {outcomeFor?.dealId === it.dealId && !it.queueItemId && (
              // No queue entry — the deal-level endpoint (399) logs it anyway,
              // same cascade. The old dead-end message is gone (UI audit 21 Aug).
              <div className="mt-2">
                <OutcomePanel
                  item={{ id: '', dealId: it.dealId, dealName: it.name, trigger: 'auto_trial' } as QueueItem}
                  direct
                  onDone={() => { setOutcomeFor(null); load(); onLogged() }}
                  onCancel={() => setOutcomeFor(null)}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

const OutcomePanel: React.FC<{ item: QueueItem; onDone: (outcome: string) => void; onCancel: () => void; direct?: boolean }> = ({ item, onDone, onCancel, direct }) => {
  const [outcome, setOutcome] = useState<string | null>(null)
  const [note, setNote] = useState('')
  const [callbackAt, setCallbackAt] = useState('')

  const needsNote = outcome === 'spoke' || outcome === 'booked'
  const submit = () => {
    if (!outcome) return
    if (needsNote && !note.trim()) { crmToast('Log what was said first — required for this outcome', 'error'); return }
    if (outcome === 'callback' && !callbackAt) { crmToast('Pick when to call back', 'error'); return }
    const payload = { outcome, note: note.trim() || null, callbackAt: callbackAt ? new Date(callbackAt).toISOString() : null }
    // direct = no queue entry: the deal-level endpoint (399) applies the same
    // effects (feed, Gate 1 stamps, engagement, team push) without a queue row.
    const p = direct
      ? logCallOutcome(item.dealId, payload)
      : queueAction(item.id, { action: 'outcome', ...payload })
    p.then(() => { crmToast('Outcome recorded', 'success'); onDone(outcome) })
      .catch((e: any) => crmToast(e?.message || 'Could not record the outcome', 'error'))
  }

  return (
    <div className="rounded-lg bg-surface-secondary border border-border p-3 space-y-2">
      <div className="flex items-center gap-1.5 flex-wrap">
        {OUTCOMES.map((o) => (
          <button
            key={o.key}
            onClick={() => setOutcome(o.key)}
            className={`px-2.5 py-1.5 rounded-lg text-[11px] font-semibold border transition-colors ${
              outcome === o.key ? 'bg-brand-600 border-brand-600 text-white' : 'border-border text-txt-secondary hover:bg-surface-hover'
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>
      {outcome === 'callback' && (
        <input type="datetime-local" value={callbackAt} onChange={(e) => setCallbackAt(e.target.value)} className="rounded-lg border border-border bg-surface px-2.5 py-1.5 text-sm text-txt-primary focus:outline-none focus:border-brand-400" />
      )}
      {outcome && (
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={needsNote ? 'What was said, next steps… (required)' : 'Note (optional)'}
          rows={2}
          className="w-full rounded-lg border border-border bg-surface px-2.5 py-1.5 text-sm text-txt-primary placeholder:text-txt-tertiary focus:outline-none focus:border-brand-400 resize-none"
        />
      )}
      <div className="flex items-center justify-end gap-2">
        <button onClick={onCancel} className="px-2.5 py-1.5 rounded-lg text-xs text-txt-secondary hover:bg-surface-hover">Cancel</button>
        <button onClick={submit} disabled={!outcome} className="px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-brand-600 hover:bg-brand-700 disabled:opacity-50">Save outcome</button>
      </div>
    </div>
  )
}

export const CrmCallQueue: React.FC = () => {
  const { currentAgent } = useAuth()
  const [items, setItems] = useState<QueueItem[] | null>(null)
  const [counts, setCounts] = useState<Record<string, number>>({})
  const [total, setTotal] = useState(0)
  const [trigger, setTrigger] = useState<string>('')
  const [q, setQ] = useState('')
  const [outcomeFor, setOutcomeFor] = useState<string | null>(null)
  const [openDeal, setOpenDeal] = useState<any | null>(null)
  // Unqualified tab: All Leads / By Event views + the forced post-call form.
  const [byEvent, setByEvent] = useState(false)
  const [eventSel, setEventSel] = useState<string | null>(null)
  const [forceQual, setForceQual] = useState<{ dealId: string; dealName: string } | null>(null)

  const load = () => {
    getCallQueue(trigger ? { trigger } : {})
      .then((r) => { setItems(r.items); setCounts(r.counts); setTotal(r.total ?? r.items.length) })
      .catch(() => { setItems([]); crmToast('Could not load the queue', 'error') })
  }
  useEffect(load, [trigger])
  useEffect(() => {
    const t = setInterval(load, 60_000)
    return () => clearInterval(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger])

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase()
    let list = items || []
    if (needle) list = list.filter((i) => `${i.dealName} ${i.contactName || ''} ${i.phone || ''} ${i.event || ''}`.toLowerCase().includes(needle))
    if (trigger === 'unqualified' && byEvent && eventSel) list = list.filter((i) => (i.event || 'No event') === eventSel)
    return list
  }, [items, q, trigger, byEvent, eventSel])

  // Event chips for the By-Event view (from the loaded unqualified items).
  const events = useMemo(() => {
    if (trigger !== 'unqualified') return []
    const m = new Map<string, number>()
    for (const i of items || []) { const k = i.event || 'No event'; m.set(k, (m.get(k) || 0) + 1) }
    return [...m.entries()].sort((a, b) => b[1] - a[1])
  }, [items, trigger])

  const claim = (it: QueueItem) =>
    queueAction(it.id, { action: it.assignedTo === String(currentAgent?.id) && it.status === 'in_progress' ? 'release' : 'claim' })
      .then(load)
      .catch(() => crmToast('Could not update the claim', 'error'))

  return (
    <div className="h-full flex flex-col bg-surface-secondary">
      <CrmToaster />
      {/* Gate 1 priority lane — ABOVE everything; fresh signups never queue. */}
      <PriorityLaneSection
        onOpenDeal={(dealId) => getDealById(dealId).then(setOpenDeal).catch(() => {})}
        onLogged={load}
      />
      <div className="px-6 pt-5 pb-3 bg-surface border-b border-border shrink-0">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-xl font-bold text-txt-primary">Call Queue</h1>
            <p className="text-sm text-txt-secondary">
              {total} lead{total === 1 ? '' : 's'} to ring — priority order: payment risk first, coldest last. Claim, dial, log the outcome.
            </p>
          </div>
          <SearchInput value={q} onChange={setQ} placeholder="Search company, contact, number…" className="w-64" />
        </div>
        <div className="flex items-center gap-1.5 flex-wrap mt-3">
          <button onClick={() => setTrigger('')} className={`px-3 py-1.5 rounded-full text-xs font-medium border ${!trigger ? 'bg-brand-600 border-brand-600 text-white' : 'border-border text-txt-secondary hover:bg-surface-hover'}`}>
            All ({total})
          </button>
          {(Object.keys(TRIGGER_META) as QueueItem['trigger'][]).map((t) => (
            <button
              key={t}
              onClick={() => { setTrigger(trigger === t ? '' : t); setEventSel(null) }}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border ${trigger === t ? 'bg-brand-600 border-brand-600 text-white' : 'border-border text-txt-secondary hover:bg-surface-hover'}`}
            >
              {TRIGGER_META[t].label} ({counts[t] || 0})
            </button>
          ))}
        </div>
        {/* Unqualified: All Leads / By Event views — pick the show, ring its list. */}
        {trigger === 'unqualified' && (
          <div className="flex items-center gap-1.5 flex-wrap mt-2.5">
            <div className="flex rounded-lg border border-border overflow-hidden">
              <button onClick={() => { setByEvent(false); setEventSel(null) }} className={`px-3 py-1.5 text-[11px] font-semibold ${!byEvent ? 'bg-violet-600 text-white' : 'bg-surface text-txt-secondary hover:bg-surface-hover'}`}>All Leads</button>
              <button onClick={() => setByEvent(true)} className={`px-3 py-1.5 text-[11px] font-semibold ${byEvent ? 'bg-violet-600 text-white' : 'bg-surface text-txt-secondary hover:bg-surface-hover'}`}>By Event</button>
            </div>
            {byEvent && events.map(([ev, n]) => (
              <button
                key={ev}
                onClick={() => setEventSel(eventSel === ev ? null : ev)}
                className={`px-2.5 py-1.5 rounded-full text-[11px] font-medium border ${eventSel === ev ? 'bg-violet-600 border-violet-600 text-white' : 'border-border text-txt-secondary hover:bg-surface-hover'}`}
              >
                {ev} ({n})
              </button>
            ))}
            {byEvent && !eventSel && events.length > 0 && <span className="text-[11px] text-txt-tertiary">← pick an event to work its list</span>}
          </div>
        )}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto p-6 space-y-2.5">
        {items === null ? (
          <p className="text-sm text-txt-tertiary">Loading the queue…</p>
        ) : rows.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border bg-surface p-10 text-center">
            <p className="text-sm text-txt-secondary">Queue clear — nothing to ring{trigger ? ' for this trigger' : ''}.</p>
            <p className="text-[11px] text-txt-tertiary mt-1">New trials, imports, expired trials and payment problems drop in automatically.</p>
          </div>
        ) : (
          rows.map((it) => {
            const t = TRIGGER_META[it.trigger]
            const mine = it.assignedTo === String(currentAgent?.id)
            const claimed = it.status === 'in_progress'
            return (
              <div key={it.id} className={`rounded-xl border bg-surface p-3.5 ${claimed && !mine ? 'opacity-60' : ''} ${mine && claimed ? 'border-brand-400' : 'border-border'}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <button onClick={() => getDealById(it.dealId).then(setOpenDeal).catch(() => {})} className="flex items-center gap-2.5 min-w-0 text-left">
                    <InitialBox name={it.dealName} />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-txt-primary truncate">{it.dealName}</p>
                      <p className="text-xs text-txt-tertiary truncate">
                        {it.contactName || '—'}{it.mrr ? ` · £${it.mrr}/mo` : ''}
                      </p>
                    </div>
                  </button>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: t.bg, color: t.fg }}>{t.label}</span>
                    {it.trigger === 'unqualified' && <QualStatusChip status={it.qualificationStatus} />}
                    {it.trigger === 'unqualified' && it.event && <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-surface-secondary text-txt-secondary border border-border" title="Captured at">{it.event}</span>}
                    {it.stage && <StageBadge stageKey={it.stage} />}
                    <span className="text-[10px] text-txt-tertiary">{it.daysInQueue === 0 ? 'today' : `${it.daysInQueue}d in queue`}</span>
                    {it.attempts > 0 && <span className="text-[10px] text-amber-600 font-semibold">attempt {it.attempts}/3</span>}
                    {it.status === 'snoozed' && it.callbackAt && (
                      <span className="text-[10px] text-txt-tertiary">↩ {new Date(it.callbackAt).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 mt-2.5 flex-wrap">
                  {it.phone ? (
                    <a href={`tel:${it.phone.replace(/[\s()-]/g, '')}`} onClick={() => !claimed && claim(it)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700">
                      <IconPhone className="w-3.5 h-3.5" /> {it.phone}
                    </a>
                  ) : (
                    <span className="px-2.5 py-1.5 rounded-lg text-[11px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">No phone number — open the lead to add one</span>
                  )}
                  <QuickBookButton dealId={it.dealId} dealName={it.dealName} onSaved={load} />
                  <button
                    onClick={() => claim(it)}
                    className={`px-2.5 py-1.5 rounded-lg text-[11px] font-semibold border transition-colors ${mine && claimed ? 'border-brand-400 text-brand-600 bg-brand-50' : 'border-border text-txt-secondary hover:bg-surface-hover'}`}
                  >
                    {claimed ? (mine ? 'Claimed by you — release' : 'Claimed') : 'Claim'}
                  </button>
                  <button onClick={() => setOutcomeFor(outcomeFor === it.id ? null : it.id)} className="px-2.5 py-1.5 rounded-lg text-[11px] font-semibold border border-border text-txt-primary hover:bg-surface-hover ml-auto">
                    Log outcome
                  </button>
                </div>

                {outcomeFor === it.id && (
                  <div className="mt-2.5">
                    <OutcomePanel
                      item={it}
                      onDone={(oc) => {
                        setOutcomeFor(null)
                        load()
                        // Spoke to an unqualified lead → the qualification form is
                        // NOT optional (Lee, 7 Aug 2026). Modal blocks until filled.
                        if (it.trigger === 'unqualified' && (oc === 'spoke' || oc === 'booked')) {
                          setForceQual({ dealId: it.dealId, dealName: it.dealName })
                        }
                      }}
                      onCancel={() => setOutcomeFor(null)}
                    />
                  </div>
                )}
              </div>
            )
          })
        )}
      </div>

      {forceQual && (
        <ForcedQualificationModal
          dealId={forceQual.dealId}
          dealName={forceQual.dealName}
          onDone={() => { setForceQual(null); load() }}
        />
      )}
      {openDeal && <LeadRedirect deal={openDeal} onDone={() => setOpenDeal(null)} />}
    </div>
  )
}
